import {
  convertEmbindVectorToArray,
  PRMarinaNode,
  PRMarinaFileArtboard,
} from '@sketch-hq/sketch-web-renderer'

/**
 * The subset of a PRFile entity the canvas overlays need, so entities read from
 * the node tree can stand in for the ones the PRFile hands back.
 */
export type OverlayEntity = Pick<
  PRMarinaFileArtboard,
  'objectId' | 'name' | 'bounds' | 'getNode'
>

const toOverlayEntity = (node: PRMarinaNode): OverlayEntity => ({
  objectId: node.getLayerUUID(),
  name: node.getLayerName(),
  bounds: node.getAbsoluteBounds(),
  getNode: () => node,
})

/**
 * The entities to overlay when a Section is focused: every Section and frame it
 * contains, at any depth, so each one can be hovered, selected and opened the
 * way it can on the page canvas. Scoped to the focused Section's own subtree -
 * nothing outside it is on the canvas.
 *
 * The overlays are stacked by containment, so a nested entity paints over its
 * ancestor and stays clickable, while the ancestor keeps whatever area its
 * children don't cover.
 *
 * Read from the node tree rather than the PRFile's frame list: that list only
 * holds frames whose Section ancestry terminates at a page, which a Preview's
 * presentation file doesn't give us, and the frames inside a Section would go
 * unoverlaid - invisible to hover, selection and navigation.
 */
export function getSectionOverlayEntities(
  sectionNode: PRMarinaNode | null
): OverlayEntity[] {
  if (!sectionNode) {
    return []
  }

  const entities: OverlayEntity[] = []

  const collectFrom = (node: PRMarinaNode) => {
    for (const child of convertEmbindVectorToArray(node.getChildren())) {
      if (child.isSection()) {
        entities.push(toOverlayEntity(child))

        // Keep going: the Sections and frames inside it are overlaid too.
        collectFrom(child)
        continue
      }

      if (child.isFrame()) {
        entities.push(toOverlayEntity(child))
        continue
      }

      // An internal rendering node (Clip, Maskees, Group Content) carries no
      // layer of its own but does nest the entities we're after.
      collectFrom(child)
    }
  }

  collectFrom(sectionNode)

  return entities
}
